import { useQuery,UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { createInfiniteQuery } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { useIndexedDB } from '@/hooks/useIndexedDB';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { filterEmptyParams, restUrl } from '@/services/utils';

import { ContactDataType } from './contact.types';

interface ContactParams {
    format?: string;
    returnKey?: string;
    returnValue?: string;
    offset?: number;
    limit?: number;
}
interface ContactResponse {
    data: ContactDataType[];
    cursor?: string;
}

/**
 * Record shape for the `contacts` store in IndexedDB. Matches AngularJS `ContactCacheService`.
 * Invalidation is server-driven (Deepstream) or same-tab (BroadcastChannel), not version polling.
 */
export interface ContactCacheRecord {
    userId: string;
    organizationId: string;
    data: ContactDataType[];
}

const CONTACTS_STORE = 'contacts';

export const useContactData = createInfiniteQuery<
    ContactResponse,
    ContactParams,
    Error,
    string | undefined
>({
    queryKey: ['contacts'],
    fetcher: async (variables, { pageParam }) => {
        const response = await axios.get(restUrl('contacts/search'), {
            params: variables,
            headers: {
                [HTTP_HEADERS.X_MESSAGE_CURSOR]: pageParam,
            },
            paramsSerializer: (params) => {
                return qs.stringify(filterEmptyParams(params));
            },
        });
        return {
            data: response.data ?? [],
            cursor:
                response.headers[HTTP_HEADERS.X_MESSAGE_CURSOR] ?? undefined,
        };
    },
    getNextPageParam: (lastPageRequestResponse) =>
        lastPageRequestResponse.cursor,
    initialPageParam: undefined,
});

export const useAllContactData = (enabled: boolean): UseQueryResult<
    ContactDataType[]
> => {
    const { user } = useUser();
    const { organization } = useOrganization();
    const cache = useIndexedDB<ContactCacheRecord>(CONTACTS_STORE);

    const userId = user && user.userId !== undefined ? String(user.userId) : undefined;
    const organizationId = organization && organization.id !== undefined ? String(organization.id) : undefined;

    return useQuery<ContactDataType[]>({
        queryKey: ['contacts', userId, organizationId],
        enabled: enabled && !!userId && !!organizationId,
        queryFn: async (): Promise<ContactDataType[]> => {
            const scopedUserId = userId as string;
            const scopedOrgId = organizationId as string;
            const cacheKey = [scopedUserId, scopedOrgId];

            const cached = await cache.get(cacheKey);
            if (cached && Array.isArray(cached.data)) {
                return cached.data;
            }

            const response = await axios.get<ContactDataType[]>(restUrl('contacts'), {
                params: {
                    state: 'ESTABLISHED',
                    role: ['DATA_RECIPIENT', 'DATA_SUPPLIER_AND_RECIPIENT'],
                },
                paramsSerializer: (params) =>
                    qs.stringify(params, { arrayFormat: 'repeat' }),
            });
            const list = response.data ?? [];
            await cache.add(cacheKey, {
                userId: scopedUserId,
                organizationId: scopedOrgId,
                data: list,
            });
            return list;
        },
    });
};
